export interface LevelItem {
  id: number;
  choice: string;
  field_label?: string;
}

export interface LevelBucket {
  label: string;
  level: number;
  parentId: number | string;
  items: LevelItem[];
  loaded: boolean;
  loading: boolean;
}

export type LevelBucketMap = Record<string, LevelBucket>;

export function getBucketKey(parentId: number | string, level: number): string {
  return `${parentId}-level-${level}`;
}

export function createEmptyBucket(
  parentId: number | string,
  level: number,
  label = ""
): LevelBucket {
  return {
    label,
    level,
    parentId,
    items: [],
    loaded: false,
    loading: false,
  };
}

export function setBucketLoading(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  label = ""
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const existing = buckets[key];
  return {
    ...buckets,
    [key]: {
      ...(existing ?? createEmptyBucket(parentId, level, label)),
      label: existing?.label || label,
      loading: true,
    },
  };
}

export function setBucketData(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  items: LevelItem[],
  label: string
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  return {
    ...buckets,
    [key]: {
      label,
      level,
      parentId,
      items,
      loaded: true,
      loading: false,
    },
  };
}

export function addItemToBucket(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  item: LevelItem,
  label?: string
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const bucket = buckets[key] ?? createEmptyBucket(parentId, level, label ?? "");
  return {
    ...buckets,
    [key]: {
      ...bucket,
      label: label ?? bucket.label,
      items: [...bucket.items, item],
      loaded: true,
      loading: false,
    },
  };
}

export function updateItemInBucket(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  itemId: number,
  updates: Partial<LevelItem>
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const bucket = buckets[key];
  if (!bucket) {
    return buckets;
  }

  return {
    ...buckets,
    [key]: {
      ...bucket,
      items: bucket.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    },
  };
}

export function removeItemFromBucket(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  itemId: number
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const bucket = buckets[key];
  if (!bucket) {
    return buckets;
  }

  return {
    ...buckets,
    [key]: {
      ...bucket,
      items: bucket.items.filter((item) => item.id !== itemId),
    },
  };
}

export function updateBucketLabel(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number,
  label: string
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const bucket = buckets[key];
  if (!bucket) {
    return buckets;
  }

  return {
    ...buckets,
    [key]: {
      ...bucket,
      label,
      items: bucket.items.map((item) => ({ ...item, field_label: label })),
    },
  };
}

export function removeBucketAndDescendants(
  buckets: LevelBucketMap,
  parentId: number | string,
  level: number
): LevelBucketMap {
  const key = getBucketKey(parentId, level);
  const bucket = buckets[key];
  if (!bucket) {
    return buckets;
  }

  let next = { ...buckets };
  delete next[key];

  bucket.items.forEach((item) => {
    Object.keys(next).forEach((bucketKey) => {
      if (bucketKey.startsWith(`${item.id}-level-`)) {
        next = removeBucketAndDescendants(
          next,
          Number(bucketKey.split("-level-")[0]),
          Number(bucketKey.split("-level-")[1])
        );
      }
    });
  });

  return next;
}

export function removeNodeDescendantBuckets(
  buckets: LevelBucketMap,
  nodeId: number
): LevelBucketMap {
  let next = { ...buckets };

  Object.keys(next).forEach((bucketKey) => {
    if (bucketKey.startsWith(`${nodeId}-level-`)) {
      const level = Number(bucketKey.split("-level-")[1]);
      next = removeBucketAndDescendants(next, nodeId, level);
    }
  });

  return next;
}